import React from "react";
import { Icon, Tooltip, Spin } from "antd";
import { useTranslation } from "react-i18next";
import * as R from "ramda";
import { connect } from "react-redux";
import { Messages } from "@worcket/common/components/listing";

import { getOptimizedImage } from "../../../utils/images";
import {
  startChatbotTestMessages,
  sendChatbotTestMessage,
  fetchChatbotCreated
} from "../../../store/actions/opportunity";
import {
  subscribeToTopic,
  unsubscribeFromTopic
} from "../../../utils/useClientConnect";
import Button from "../../Button";
import { ACCEPT_LANGUAGE } from "../../../locales/acceptedLanguages";

import styled from "styled-components/macro";

export const CHATBOT_TEST_STATUSES = {
  LOADING: "LOADING",
  INVALID_TREE: "INVALID_TREE",
  SAVE_CHANGES: "SAVE_CHANGES",
  WAIT_CREATION: "WAIT_CREATION",
  WAIT_FIRST_MESSAGE: "WAIT_FIRST_MESSAGE",
  READY: "READY"
};

const Chat = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 340px;
  flex-shrink: 0;
  justify-content: center;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  line-height: initial;
  position: relative;
  box-shadow: -5px 0px 10px -2px #d6d6d68c;
`;

const TestDisabled = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  z-index: 1;
  text-align: center;

  .title {
    font-size: 16px;
    margin-bottom: 4px;
  }

  .warning-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }

  & > button {
    margin-top: 14px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 16px;
  height: 42px;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;

  .title {
    margin: 0;
    font-size: 16px;
  }
`;

const MessagesContainer = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  overflow: auto;
`;

const HeaderButtons = styled.div`
  display: flex;
`;

const UnstyledButton = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: 16px;
  margin-left: 12px;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme["@primary"]};
  }
`;

function ChatbotTest({
  showTestSidebar,
  testStatus,
  setTestStatus,
  messages,
  idCompany,
  companyAvatar,
  idOpportunity,
  startChatbotTestMessages,
  sendChatbotTestMessage,
  id,
  onSave,
  loading,
  isChatbotCreated,
  fetchChatbotCreated
}) {
  const { t, i18n } = useTranslation(["opportunity"]);
  const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;

  const topics = [
    `test/${idCompany}/${idOpportunity}/${id}/test`,
    `test/${idCompany}/${idOpportunity}/${id}`
  ];

  React.useEffect(() => {
    subscribeToTopic(topics);
    fetchChatbotCreated();

    return () => {
      unsubscribeFromTopic(topics);
    };
    // eslint-disable-next-line
  }, []);

  // Send the init conversation request if the chatbot is already created
  // when entering the screen or after receiving the [chatbot_created] ws message
  React.useEffect(() => {
    if (
      isChatbotCreated &&
      [
        CHATBOT_TEST_STATUSES.LOADING,
        CHATBOT_TEST_STATUSES.WAIT_CREATION
      ].includes(testStatus)
    ) {
      startChatbotTestMessages();
      setTestStatus(CHATBOT_TEST_STATUSES.WAIT_FIRST_MESSAGE);
    }
  }, [isChatbotCreated, setTestStatus, startChatbotTestMessages, testStatus]);

  React.useEffect(() => {
    if (
      messages.length >= 1 &&
      testStatus === CHATBOT_TEST_STATUSES.WAIT_FIRST_MESSAGE
    ) {
      setTestStatus(CHATBOT_TEST_STATUSES.READY);
    }
  }, [testStatus, messages, setTestStatus]);

  return (
    <Chat style={{ display: showTestSidebar ? "flex" : "none" }}>
      <Header>
        <h4 className="title">
          {t("opportunity:process.stepTypes.chatbot.test.title")}
        </h4>
        <HeaderButtons>
          {testStatus === CHATBOT_TEST_STATUSES.READY && (
            <Tooltip
              title={t("opportunity:process.stepTypes.chatbot.test.reset")}
              placement="left"
            >
              <UnstyledButton
                type="button"
                onClick={() => {
                  startChatbotTestMessages();
                }}
              >
                <Icon type="reload" />
              </UnstyledButton>
            </Tooltip>
          )}
        </HeaderButtons>
      </Header>
      <MessagesContainer>
        {[
          CHATBOT_TEST_STATUSES.LOADING,
          CHATBOT_TEST_STATUSES.INVALID_TREE,
          CHATBOT_TEST_STATUSES.SAVE_CHANGES,
          CHATBOT_TEST_STATUSES.WAIT_CREATION,
          CHATBOT_TEST_STATUSES.WAIT_FIRST_MESSAGE
        ].includes(testStatus) && (
          <TestDisabled>
            {testStatus === CHATBOT_TEST_STATUSES.INVALID_TREE ? (
              <>
                <Icon type="warning" className="warning-icon" />
                <h5 className="title">
                  {t("opportunity:process.stepTypes.chatbot.test.invalidTree")}
                </h5>
                <span>
                  {t(
                    "opportunity:process.stepTypes.chatbot.test.correctErrorsToTest"
                  )}
                </span>
              </>
            ) : testStatus === CHATBOT_TEST_STATUSES.SAVE_CHANGES ? (
              <>
                <Icon type="save" className="warning-icon" />
                <h5 className="title">
                  {t("opportunity:process.stepTypes.chatbot.test.saveChanges")}
                </h5>
                <span>
                  {t("opportunity:process.stepTypes.chatbot.test.saveRequired")}
                </span>
                <Button
                  onClick={() =>
                    onSave({
                      onResolved: () =>
                        setTestStatus(CHATBOT_TEST_STATUSES.WAIT_CREATION)
                    })
                  }
                  loading={loading}
                >
                  {t("global:save")}
                </Button>
              </>
            ) : [
                CHATBOT_TEST_STATUSES.LOADING,
                CHATBOT_TEST_STATUSES.WAIT_CREATION,
                CHATBOT_TEST_STATUSES.WAIT_FIRST_MESSAGE
              ].includes(testStatus) ? (
              <>
                <Spin style={{ marginBottom: 8 }} />
                <span>
                  {t(
                    "opportunity:process.stepTypes.chatbot.test.initializing1"
                  )}
                  <br />
                  {t(
                    "opportunity:process.stepTypes.chatbot.test.initializing2"
                  )}
                </span>
              </>
            ) : null}
          </TestDisabled>
        )}
        <Messages
          t={t}
          locale={locale}
          messages={R.map(message => ({
            ...message,
            subtext:
              message.chatbot &&
              t("opportunity:process.stepDetails.automaticMessage")
          }))(messages)}
          candidate="test-candidate-id"
          company={idCompany}
          opportunity={idOpportunity}
          avatars={{ company: companyAvatar }}
          sender="candidate"
          onSend={message => {
            sendChatbotTestMessage(message);
          }}
          showDayDivider={false}
          showMessageTime={false}
        />
      </MessagesContainer>
    </Chat>
  );
}

export default connect(
  (state, { idOpportunity }) => ({
    messages: R.pathOr(
      [],
      ["opportunity", "selected", "selectedStep", "messages"],
      state
    ),
    isChatbotCreated: R.pathOr(
      false,
      ["opportunity", "selected", "selectedStep", "isChatbotCreated"],
      state
    ),
    idUser: R.path(["user", "id"], state),
    idCompany: R.path(["company", "id"], state),
    companyAvatar: getOptimizedImage(R.path(["company", "logo"], state)),
    idOpportunity:
      idOpportunity || R.path(["opportunity", "selected", "id"], state)
  }),
  {
    startChatbotTestMessages,
    sendChatbotTestMessage,
    fetchChatbotCreated
  }
)(ChatbotTest);
